import { z } from 'zod'

import { ApiClientError } from '@ems/api-client/client'
import type { RegistrationForm } from '@ems/contracts/profile-fields'
import type { StatusTone } from '@ems/ui/patterns/status-badge'

import type { PaymentMethod } from '#admin/features/billing/payments-api.ts'
import {
  answersOf,
  type DraftValue,
  type ParticipantDraft,
} from '#admin/features/registrations/participant-fields.tsx'
import { api } from '#admin/lib/api-client.ts'

/**
 * Registrations, with **all three state axes** (invariant 06).
 *
 * The console renders them as three columns because they are three facts. A UI that collapsed
 * them into one badge would be the first place the axes got flattened again — and the
 * combination that matters, "paid but awaiting approval", is exactly the one a single badge
 * has to pick a side on.
 */
export const REGISTRATION_STATUSES = ['draft', 'pending_payment', 'completed', 'canceled'] as const
export const PAYMENT_STATUSES = ['not_required', 'pending', 'paid', 'failed', 'refunded'] as const
export const VERIFICATION_STATUSES = ['not_required', 'pending', 'approved', 'rejected'] as const

/** What the list endpoint sends. No participants: the list does not carry them. */
export const registrationSummarySchema = z.object({
  id: z.string(),
  code: z.string().nullable(),
  status: z.enum(REGISTRATION_STATUSES),
  paymentStatus: z.enum(PAYMENT_STATUSES),
  verificationStatus: z.enum(VERIFICATION_STATUSES),
  contactName: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string().nullable(),
  currency: z.string(),
  subtotal: z.number(),
  discountTotal: z.number(),
  taxTotal: z.number(),
  total: z.number(),
  voucherCode: z.string().nullable(),
  invoiceId: z.string().nullable(),
  paymentProofKey: z.string().nullable(),
  submittedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  canceledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  verificationNote: z.string().nullable(),
  createdAt: z.string(),
})

export const participantSchema = z.object({
  id: z.string(),
  ticketTypeId: z.string(),
  fullName: z.string(),
  email: z.string(),
  badgeName: z.string().nullable(),
  jobTitle: z.string().nullable(),
  companyName: z.string().nullable(),
  phone: z.string().nullable(),
  nationality: z.string().nullable(),
  dietaryRequirements: z.string().nullable(),
  memberId: z.string().nullable(),
  associationName: z.string().nullable(),
  /** Sensitive answers arrive masked; `maskedAnswers` names them. */
  answers: z.record(z.string(), z.unknown()),
  maskedAnswers: z.array(z.string()),
  unitPrice: z.number(),
  discountAmount: z.number(),
  lineTotal: z.number(),
})

/** One registration as the detail endpoint sends it — always with its delegation. */
export const registrationSchema = registrationSummarySchema.extend({
  participants: z.array(participantSchema),
})

export type RegistrationSummary = z.infer<typeof registrationSummarySchema>
export type Registration = z.infer<typeof registrationSchema>
export type Participant = z.infer<typeof participantSchema>

const registrationPageSchema = z.object({
  items: z.array(registrationSummarySchema),
  nextCursor: z.string().nullable(),
  /** The whole filtered set, whichever page this is. */
  total: z.number(),
})

export type RegistrationPage = z.infer<typeof registrationPageSchema>

// ponytail: mirrors `TICKET_STATUSES` in @ems/db, which admin does not depend on.
export const TICKET_STATUSES = [
  'pending',
  'active',
  'canceled',
  'checked_in',
  'expired',
  'transferred',
] as const

const issuedTicketSchema = z.object({
  id: z.string(),
  code: z.string(),
  status: z.enum(TICKET_STATUSES),
  holderName: z.string(),
  /** The shorter name the badge shows. Null when the participant gave none. */
  badgeName: z.string().nullable(),
  ticketTypeId: z.string(),
  checkedInAt: z.string().nullable(),
})

export type IssuedTicket = z.infer<typeof issuedTicketSchema>

/** The worklist's filters, exactly as they sit in the URL. Empty means "any". */
export type RegistrationFilters = {
  q: string
  status: RegistrationSummary['status'] | ''
  paymentStatus: RegistrationSummary['paymentStatus'] | ''
  verificationStatus: RegistrationSummary['verificationStatus'] | ''
  ticketTypeId: string
}

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

/**
 * Optional strings go on the wire only when filled. An empty badge name would otherwise be
 * stored as `''` and printed as a blank lanyard, where omitting it keeps what is there.
 */
const optional = (key: string, value: string) =>
  value.trim() === '' ? {} : { [key]: value.trim() }
const base = '/v1/ticket/admin/registrations'

export function fetchRegistrations(
  filters: RegistrationFilters,
  cursor: string | undefined,
  { signal, limit = 50 }: { signal?: AbortSignal; limit?: number } = {},
): Promise<RegistrationPage> {
  const params = new URLSearchParams({ limit: String(limit) })
  for (const [key, value] of Object.entries(filters)) if (value !== '') params.set(key, value)
  if (cursor !== undefined) params.set('cursor', cursor)
  return api.get(`${base}?${params.toString()}`, {
    schema: registrationPageSchema,
    ...withSignal(signal),
  })
}

export function fetchRegistration(id: string, signal?: AbortSignal): Promise<Registration> {
  return api.get(`${base}/${id}`, { schema: registrationSchema, ...withSignal(signal) })
}

export function fetchRegistrationTickets(id: string, signal?: AbortSignal) {
  return api.get(`${base}/${id}/tickets`, {
    schema: z.object({ items: z.array(issuedTicketSchema) }),
    ...withSignal(signal),
  })
}

const historyEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
  note: z.string().nullable(),
})

export type HistoryEntry = z.infer<typeof historyEntrySchema>

export function fetchRegistrationHistory(id: string, signal?: AbortSignal) {
  return api.get(`${base}/${id}/history`, {
    schema: z.object({ items: z.array(historyEntrySchema) }),
    ...withSignal(signal),
  })
}

/** The audit actions a registration's history carries. Anything else reads as its own name. */
const HISTORY_LABELS: Record<string, string> = {
  'ticket.registration_created': 'Created',
  'ticket.registration_contact_updated': 'Contact updated',
  'ticket.participants_replaced': 'Participants changed',
  'ticket.participants_added': 'Participants added',
  'ticket.participant_updated': 'Participant edited',
  'ticket.participant_deleted': 'Participant removed',
  'ticket.registration_submitted': 'Submitted',
  'ticket.invoice_created': 'Invoice issued',
  'ticket.invoice_recreated': 'Invoice replaced',
  'ticket.payment_proof_attached': 'Proof of transfer attached',
  'ticket.payment_confirmed': 'Payment confirmed',
  'ticket.payment_confirm_rejected': 'Payment refused',
  'ticket.tickets_issued': 'Tickets issued',
  'ticket.verification_approved': 'Verification approved',
  'ticket.verification_rejected': 'Verification rejected',
  'ticket.verification_reopened': 'Verification reopened',
  'ticket.voucher_applied': 'Voucher applied',
  'ticket.voucher_removed': 'Voucher removed',
  'ticket.registration_canceled': 'Canceled',
  // Audit rows written before the US spelling change keep the old action name.
  'ticket.registration_cancelled': 'Canceled',
  'ticket.sensitive_viewed': 'Sensitive answers viewed',
  'ticket.registration_emailed': 'Email sent',
  'ticket.registration_email_failed': 'Email not sent',
}

export function historyLabel(action: string): string {
  return HISTORY_LABELS[action] ?? label(action.replace(/^ticket\./, ''))
}

/**
 * The operator verbs.
 *
 * Every one answers with the summary schema, **never** the detail one: `confirm-payment`,
 * `verification`, `cancel` and `voucher` return the registration without its participants, so
 * a caller that wrote the response into the detail cache would silently drop the delegation.
 * The mutations invalidate instead.
 */
/** How the payer will pay, and who the invoice is to. Sent only when submit issues an invoice. */
/** `paymentMethodCode` absent: billing selects the one method that is on. */
export type SubmitBody = { paymentMethodCode?: string; billToName: string; billToEmail: string }

export function submitRegistration(id: string, body?: SubmitBody) {
  return api.post(`${base}/${id}/submit`, {
    schema: registrationSummarySchema,
    ...(body === undefined ? {} : { body }),
  })
}

/**
 * The payment that the operator received. Every field is optional. With an invoice and billing
 * on, the API records it as a billing payment against the invoice.
 */
export type ConfirmPaymentBody = {
  method?: PaymentMethod
  /** In the invoice currency. Absent means the balance of the invoice. */
  amount?: string
  paidAt?: string
  /** Absent means the invoice number. */
  reference?: string
  payerName?: string
  proofKey?: string
}

export function confirmPayment(id: string, body?: ConfirmPaymentBody) {
  return api.post(`${base}/${id}/confirm-payment`, {
    schema: registrationSummarySchema,
    ...(body === undefined ? {} : { body }),
  })
}

/** Voids the invoice, unless billing voided it already, and issues a new one (#57). */
export function reinvoiceRegistration(id: string, reason: string) {
  return api.post(`${base}/${id}/reinvoice`, {
    schema: registrationSummarySchema,
    body: { reason },
  })
}

export type EmailKind = 'payment_completed' | 'ticket_issued' | 'registration_pending'

/** Sends a registration email again (#56). The answer names each address that got it. */
export function resendEmail(id: string, kind: EmailKind, participantId?: string) {
  return api.post(`${base}/${id}/resend-email`, {
    schema: z.object({ sentTo: z.array(z.string()) }),
    body: { kind, ...(participantId === undefined ? {} : { participantId }) },
  })
}

export function decideVerification(id: string, approved: boolean, note: string) {
  return api.post(`${base}/${id}/verification`, {
    schema: registrationSummarySchema,
    body: { approved, ...(note === '' ? {} : { note }) },
  })
}

export function reopenVerification(id: string) {
  return api.post(`${base}/${id}/verification/reopen`, { schema: registrationSummarySchema })
}

export function cancelRegistration(id: string, reason: string) {
  return api.post(`${base}/${id}/cancel`, { schema: registrationSummarySchema, body: { reason } })
}

export function deleteRegistration(id: string) {
  return api.delete(`${base}/${id}`, { schema: z.undefined() })
}

export function applyVoucher(id: string, code: string) {
  return api.post(`${base}/${id}/voucher`, { schema: registrationSummarySchema, body: { code } })
}

export function removeVoucher(id: string) {
  return api.delete(`${base}/${id}/voucher`, { schema: registrationSummarySchema })
}

export type ContactCorrection = { contactName: string; contactEmail: string; contactPhone: string }

/** A blank phone is left out, not sent: the API keeps what is stored for an omitted field. */
export function updateContact(id: string, input: ContactCorrection) {
  return api.patch(`${base}/${id}`, {
    schema: registrationSummarySchema,
    body: {
      contactName: input.contactName.trim(),
      contactEmail: input.contactEmail.trim(),
      ...optional('contactPhone', input.contactPhone),
    },
  })
}

/**
 * A correction to one participant: their details and answers, never their ticket or its price.
 *
 * `before` is what the sheet opened with, so a cleared answer is sent as `null` (the API's word
 * for "remove it") and an untouched one is left out rather than resent.
 */
export type ParticipantCorrection = {
  form: RegistrationForm
  ticketTypeId: string
  draft: ParticipantDraft
  before: ParticipantDraft
}

const blankToNull = (value: string) => (value.trim() === '' ? null : value.trim())

export function correctionBody({ form, ticketTypeId, draft, before }: ParticipantCorrection) {
  const answers: Record<string, unknown> = { ...answersOf(form, ticketTypeId, draft.answers) }
  for (const key of Object.keys(answersOf(form, ticketTypeId, before.answers))) {
    if (!(key in answers)) answers[key] = null
  }
  return {
    fullName: draft.fullName.trim(),
    email: draft.email.trim(),
    phone: blankToNull(draft.standard.phone),
    nationality: blankToNull(draft.standard.nationality),
    badgeName: blankToNull(draft.standard.badgeName),
    jobTitle: blankToNull(draft.standard.jobTitle),
    companyName: blankToNull(draft.standard.companyName),
    dietaryRequirements: blankToNull(draft.standard.dietaryRequirements),
    answers,
  }
}

export function updateParticipant(id: string, participantId: string, input: ParticipantCorrection) {
  return api.patch(`${base}/${id}/participants/${participantId}`, {
    schema: registrationSchema,
    body: correctionBody(input),
  })
}

/** Remove one participant from a draft. The server reprices what is left. */
export function deleteParticipant(id: string, participantId: string) {
  return api.delete(`${base}/${id}/participants/${participantId}`, { schema: registrationSchema })
}

/** A stored participant, in the form's editable shape. `revealed` fills masked answers. */
export function draftOf(
  participant: Participant,
  revealed: Record<string, unknown> = {},
): ParticipantDraft {
  const answers: Record<string, DraftValue> = {}
  for (const [key, value] of Object.entries({ ...participant.answers, ...revealed })) {
    if (typeof value === 'number') answers[key] = String(value)
    else if (typeof value === 'string' || typeof value === 'boolean') answers[key] = value
    else if (Array.isArray(value)) answers[key] = value.filter((v) => typeof v === 'string')
  }
  return {
    fullName: participant.fullName,
    email: participant.email,
    standard: {
      phone: participant.phone ?? '',
      nationality: participant.nationality ?? '',
      badgeName: participant.badgeName ?? '',
      jobTitle: participant.jobTitle ?? '',
      companyName: participant.companyName ?? '',
      dietaryRequirements: participant.dietaryRequirements ?? '',
    },
    answers,
  }
}

/**
 * A participant's sensitive answers, in full. **Audited** — the API records who looked at whose,
 * which is why this is fetched on a click and never on page load.
 */
export function fetchSensitiveAnswers(id: string, participantId: string) {
  return api.get(`${base}/${id}/participants/${participantId}/sensitive`, {
    schema: z.object({ answers: z.record(z.string(), z.unknown()) }),
  })
}

/** A five-minute link to a file a participant uploaded for a question. Fetched on click. */
export function fetchParticipantFileLink(id: string, participantId: string, fieldKey: string) {
  return api.get(`${base}/${id}/participants/${participantId}/files/${fieldKey}`, {
    schema: z.object({ url: z.string(), expiresInSeconds: z.number() }),
  })
}

/**
 * The per-field issues a refused submit or save carries (`detail.issues`), read defensively: the
 * detail is untyped on the wire, and a shape this does not recognize is simply no issues.
 */
export type FieldIssue = {
  participantId: string | null
  participantIndex: number | null
  participantName: string
  field: string
  message: string
}

export function issuesOf(error: unknown): FieldIssue[] {
  if (!(error instanceof ApiClientError)) return []
  const raw = error.detail?.['issues']
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item: unknown) => {
    if (typeof item !== 'object' || item === null) return []
    const read = (key: string): unknown => Reflect.get(item, key)
    const field = read('field')
    const message = read('fieldMessage') ?? read('message')
    if (typeof field !== 'string' || typeof message !== 'string') return []
    const participantId = read('participantId')
    const participantIndex = read('participantIndex')
    const participantName = read('participantName')
    return [
      {
        participantId: typeof participantId === 'string' ? participantId : null,
        participantIndex: typeof participantIndex === 'number' ? participantIndex : null,
        participantName: typeof participantName === 'string' ? participantName : 'A participant',
        field,
        message,
      },
    ]
  })
}

/** A five-minute link to the proof of transfer. Fetched on click, never rendered on load. */
export function fetchPaymentProofLink(id: string) {
  return api.get(`${base}/${id}/payment-proof`, {
    schema: z.object({ url: z.string(), expiresInSeconds: z.number() }),
  })
}

// --- bulk import --------------------------------------------------------------------------

const bulkImportResultSchema = z.object({
  imported: z.number(),
  registrationIds: z.array(z.string()),
  failures: z.array(z.object({ lines: z.array(z.number()), reason: z.string() })),
  unmatchedColumns: z.array(z.string()),
})

export type BulkImportResult = z.infer<typeof bulkImportResultSchema>
export type ImportKind = 'priced' | 'exhibitor-pass'

export function bulkImport(kind: ImportKind, csv: string): Promise<BulkImportResult> {
  return api.post(`${base}/${kind === 'priced' ? 'bulk' : 'bulk-exhibitor-pass'}`, {
    schema: bulkImportResultSchema,
    body: { csv },
  })
}

/** Straight from the parser (`apps/api/src/ticket/registrations/import.ts`), not from memory. */
export const IMPORT_REQUIRED_COLUMNS = [
  'contactName',
  'contactEmail',
  'ticketTypeCode',
  'fullName',
  'email',
] as const
export const IMPORT_OPTIONAL_COLUMNS = [
  'badgeName',
  'jobTitle',
  'companyName',
  'memberId',
  'associationName',
  'phone',
  'nationality',
  'dietaryRequirements',
] as const

/** "Lines 14, 15 — no ticket type with code EARLY-BIRD". What the results panel lists. */
export function describeFailure(failure: BulkImportResult['failures'][number]): string {
  const lines = failure.lines.join(', ')
  return `${failure.lines.length === 1 ? 'Line' : 'Lines'} ${lines} — ${failure.reason}`
}

// --- walk-in -------------------------------------------------------------------------------

export type WalkInParticipant = { ticketTypeId: string; draft: ParticipantDraft }

export const personMatchSchema = z.object({ id: z.string(), name: z.string(), email: z.string() })
export type PersonMatch = z.infer<typeof personMatchSchema>

/** This edition's delegates by name or email — the only people a walk-in can be linked to. */
export function fetchPeople(q: string, signal?: AbortSignal) {
  return api.get(`/v1/ticket/admin/people?q=${encodeURIComponent(q)}`, {
    schema: z.object({ items: z.array(personMatchSchema) }),
    ...withSignal(signal),
  })
}

export type WalkIn = {
  /** The delegate account it belongs to. Omitted for a booking with no account behind it. */
  personId?: string
  contactName: string
  contactEmail: string
  contactPhone: string
  currency: string
  participants: WalkInParticipant[]
  form: RegistrationForm
}

export function createRegistration(input: WalkIn) {
  return api.post(base, {
    schema: registrationSummarySchema,
    body: {
      contactName: input.contactName.trim(),
      contactEmail: input.contactEmail.trim(),
      ...optional('contactPhone', input.contactPhone),
      ...optional('personId', input.personId ?? ''),
      currency: input.currency,
      participants: input.participants.map((participant) =>
        participantBody(input.form, participant),
      ),
    },
  })
}

/** One new participant as `ParticipantInput`: blanks left out, answers only those that apply. */
function participantBody(form: RegistrationForm, { ticketTypeId, draft }: WalkInParticipant) {
  return {
    ticketTypeId,
    fullName: draft.fullName.trim(),
    email: draft.email.trim(),
    ...optional('phone', draft.standard.phone),
    ...optional('nationality', draft.standard.nationality),
    ...optional('badgeName', draft.standard.badgeName),
    ...optional('jobTitle', draft.standard.jobTitle),
    ...optional('companyName', draft.standard.companyName),
    ...optional('dietaryRequirements', draft.standard.dietaryRequirements),
    answers: answersOf(form, ticketTypeId, draft.answers),
  }
}

/** More participants on a draft, after the others. Answers with the whole delegation. */
export function addParticipants(
  id: string,
  form: RegistrationForm,
  participants: readonly WalkInParticipant[],
) {
  return api.post(`${base}/${id}/participants`, {
    schema: registrationSchema,
    body: { participants: participants.map((participant) => participantBody(form, participant)) },
  })
}

// --- what an operator may do next ------------------------------------------------------------

/**
 * What an operator should do next, derived from all three axes together.
 *
 * This is the reason the axes stay separate all the way to the UI: the work queue is not
 * "unpaid registrations", it is "paid ones nobody has approved yet" and "approved ones nobody
 * has paid for" — two different piles that a flattened status cannot tell apart.
 */
export function awaitingAction(registration: RegistrationSummary): string | null {
  if (registration.status !== 'pending_payment') return null
  if (registration.verificationStatus === 'pending') return 'Awaiting approval'
  // Not "ready": completion needs approval, and a rejection is the operator's call to chase.
  if (registration.verificationStatus === 'rejected') return 'Verification rejected'
  if (registration.paymentStatus === 'pending') return 'Awaiting payment'
  if (registration.paymentStatus === 'failed') return 'Payment failed'
  return 'Ready to confirm'
}

export type AvailableActions = {
  /** A walk-in the operator drafted; nobody else is signed in to submit it. */
  submit: boolean
  /**
   * `confirm-payment` settles what is owed and completes the registration if nothing else is
   * outstanding — so it is two different buttons in words. `null` when it would do nothing: a
   * paid registration still awaiting approval gains nothing from a second confirm.
   */
  confirmPayment: 'confirm' | 'complete' | null
  decideVerification: boolean
  /** A rejection is not final: the delegate sends a clearer document and review starts again. */
  reopenVerification: boolean
  cancel: boolean
  /** The API applies a voucher only while the registration is a draft. */
  applyVoucher: boolean
  /** Removed under the same draft-only rule, and only when there is one. */
  removeVoucher: boolean
  /** An operator may correct it after submit; not once canceled or on an invoice. */
  editContact: boolean
  /** A canceled delegate has nothing left to correct. Checked-in holders the API refuses. */
  editParticipants: boolean
  /** Draft only: after submit, another person changes what is owed — a cancel and rebook. */
  addParticipant: boolean
  /** An unpaid invoice is replaced, also after billing voided it (#57). Needs billing `full`. */
  reinvoice: boolean
  /** Only a completed registration has tickets, so only it has a confirmation and tickets to send. */
  resendTickets: boolean
  /** The approval request goes to the staff while the verification waits. */
  resendApproval: boolean
}

/**
 * Which verbs the header offers, from the three axes.
 *
 * The same rules v1's detail page used, and the server enforces each one again: an action the
 * page offers and the API refuses is a toast; an action the page withholds that the API would
 * take is an operator ringing support. Delete is not here because it is always offered — the
 * API's 409 on live tickets is the message, not a missing button.
 */
/**
 * Nothing offered — for a role that may view registrations and not change them. The page swaps this
 * in for `availableActions` rather than checking the permission at every button.
 */
export const NO_ACTIONS: AvailableActions = {
  submit: false,
  confirmPayment: null,
  decideVerification: false,
  reopenVerification: false,
  cancel: false,
  applyVoucher: false,
  removeVoucher: false,
  editContact: false,
  editParticipants: false,
  addParticipant: false,
  reinvoice: false,
  resendTickets: false,
  resendApproval: false,
}

export function availableActions(registration: RegistrationSummary): AvailableActions {
  const { status, paymentStatus, verificationStatus } = registration
  const owed = paymentStatus === 'pending' || paymentStatus === 'failed'
  // The machine's `canComplete` guard, read from the same two axes.
  const settled =
    (paymentStatus === 'paid' || paymentStatus === 'not_required') &&
    (verificationStatus === 'approved' || verificationStatus === 'not_required')

  let confirm: AvailableActions['confirmPayment'] = null
  if (status === 'pending_payment' && owed) confirm = 'confirm'
  else if (status === 'pending_payment' && settled) confirm = 'complete'

  return {
    submit: status === 'draft',
    confirmPayment: confirm,
    decideVerification: status !== 'canceled' && verificationStatus === 'pending',
    reopenVerification: status !== 'canceled' && verificationStatus === 'rejected',
    cancel: status !== 'canceled' && status !== 'completed',
    applyVoucher: status === 'draft',
    removeVoucher: status === 'draft' && registration.voucherCode !== null,
    editContact: status !== 'canceled' && registration.invoiceId === null,
    editParticipants: status !== 'canceled',
    addParticipant: status === 'draft',
    reinvoice: status !== 'canceled' && owed && registration.invoiceId !== null,
    resendTickets: status === 'completed',
    resendApproval: status !== 'canceled' && verificationStatus === 'pending',
  }
}

/**
 * A refused delete, in words that say what to do.
 *
 * Branches on the code and the `detail` the API attaches (`liveTickets`), never on the message:
 * the server can reword its sentence without this breaking.
 */
export function isLiveTicketsRefusal(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    error.code === 'conflict' &&
    typeof error.detail?.['liveTickets'] === 'number'
  )
}

// --- vocabulary ------------------------------------------------------------------------------

/**
 * What each of the three axes means to somebody working a queue.
 *
 * **The mapping lives here rather than in the component**, because what `pending_payment` means is
 * knowledge that belongs to registrations — the same word means something different on an invoice
 * and on a stand booking, and a map inside a shared component library would be that library quietly
 * owning the product's vocabulary.
 *
 * `warning` is "somebody has to act and it is not an error yet"; `pending` is "waiting on somebody
 * else". The distinction is the whole reason an operator opens this screen, so the two tones are
 * deliberately different.
 */
const REGISTRATION_TONE: Record<RegistrationSummary['status'], StatusTone> = {
  completed: 'success',
  pending_payment: 'warning',
  canceled: 'danger',
  draft: 'neutral',
}

export function registrationTone(status: RegistrationSummary['status']): StatusTone {
  return REGISTRATION_TONE[status]
}

const PAYMENT_TONE: Record<RegistrationSummary['paymentStatus'], StatusTone> = {
  paid: 'success',
  pending: 'warning',
  failed: 'danger',
  refunded: 'info',
  // Not every registration is chargeable, and "nothing to pay" is not an achievement — so it reads
  // as neutral rather than green, which would otherwise put a tick beside a free ticket.
  not_required: 'neutral',
}

export function paymentTone(status: RegistrationSummary['paymentStatus']): StatusTone {
  return PAYMENT_TONE[status]
}

const VERIFICATION_TONE: Record<RegistrationSummary['verificationStatus'], StatusTone> = {
  approved: 'success',
  // Waiting on an operator to look at documents — this is the queue itself.
  pending: 'warning',
  rejected: 'danger',
  not_required: 'neutral',
}

const TICKET_TONE: Record<IssuedTicket['status'], StatusTone> = {
  active: 'success',
  checked_in: 'info',
  pending: 'warning',
  canceled: 'danger',
  expired: 'neutral',
  transferred: 'neutral',
}

export function ticketTone(status: IssuedTicket['status']): StatusTone {
  return TICKET_TONE[status]
}

export function verificationTone(status: RegistrationSummary['verificationStatus']): StatusTone {
  return VERIFICATION_TONE[status]
}

/** `draft` → `draft`, `pending_payment` → `pending payment`. The API's vocabulary, made readable. */
export function label(value: string): string {
  return value.replaceAll('_', ' ')
}
